import React, { useRef, useEffect, useState } from 'react';
import AgentNarrative from './components/AgentNarrative';
import NET from 'vanta/dist/vanta.net.min';

/**
 * Vision Section Component
 * 
 * Wraps AgentNarrative with intersection observer to start animation when in view
 * and restarts animation when it completes
 */
const VisionSection = () => {
    const sectionRef = useRef(null);
    const narrativeRef = useRef(null);
    const [isInView, setIsInView] = useState(false);

    // Start animation when section comes into view
    useEffect(() => {
        if (isInView && narrativeRef.current) {
            // Use requestAnimationFrame to ensure smooth start without showing button
            requestAnimationFrame(() => {
                if (narrativeRef.current) {
                    narrativeRef.current.start();
                }
            });
        }
    }, [isInView]);

    // Set up intersection observer
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsInView(true);
                    } else {
                        setIsInView(false);
                    }
                });
            },
            {
                threshold: 0.2, // Start when 20% of section is visible
            }
        );

        const currentSection = sectionRef.current;
        if (currentSection) {
            observer.observe(currentSection);
        }

        return () => {
            if (currentSection) {
                observer.unobserve(currentSection);
            }
        };
    }, []);

    const handleComplete = () => {
        // Auto restart animation immediately (start() already calls reset() internally)
        // if (narrativeRef.current && isInView) {
        //     // Use requestAnimationFrame to ensure smooth restart without showing button
        //     requestAnimationFrame(() => {
        //         if (narrativeRef.current) {
        //             narrativeRef.current.start();
        //         }
        //     });
        // }
    };

    return (
        <section ref={sectionRef} className="py-16 lg:py-24 bg-white dark:bg-gray-950">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <div className="mb-12 text-center">
                    <h2 className="text-h2 md:text-h1 font-display font-bold text-gray-900 dark:text-white mb-4">
                        Our vision for the future
                    </h2>
                </div>
                <style>{`
                    /* Hide the play button - it should never show */
                    .vision-section-wrapper div.mt-6.flex.justify-center {
                        display: none !important;
                    }
                `}</style>
                <div className="vision-section-wrapper">
                    <AgentNarrative
                        ref={narrativeRef}
                        phases={[
                            {
                                id: 'discovery',
                                title: 'Discovery Sweep',
                                subtitle: 'Scanning data sources for relevant information',
                                sources: ['Jira', 'Confluence', 'Slack'],
                                steps: [
                                    {
                                        id: 'discovery-1',
                                        text: 'I managed to identify the available logs, the license required, and the available ingestion methods',
                                    },
                                    {
                                        id: 'discovery-2',
                                        text: 'I\'ve verified that you are already ingesting the logs',
                                    },
                                    {
                                        id: 'discovery-3',
                                        text: 'I\'ve identified the key features of the application',
                                    },
                                    {
                                        id: 'discovery-4',
                                        text: 'I\'ve correlated that with the functionality used in the organization',
                                    },
                                    {
                                        id: 'discovery-5',
                                        text: 'I\'ve found 3 threat reports that discuss CI/CD compromise and summarized the findings here (https://threatreports.respondnt.io/summary/abc456)',
                                    },
                                    {
                                        id: 'discovery-6',
                                        text: 'I\'ve created a threat model according to PASTA, ready for review (https://threatmodel.respondnt.io/review/xyz789)',
                                    },
                                ],
                            },
                            {
                                id: 'signals',
                                title: 'Knowledge Found',
                                subtitle: 'Correlating findings across platforms',
                                sources: ['Jira', 'Confluence', 'Slack', 'CI/CD'],
                                steps: [
                                    {
                                        id: 'signals-1',
                                        text: 'I found this JIRA ticket that talks about creating software to generate API tokens. It was written 3 months ago and updated once in the last week',
                                    },
                                    {
                                        id: 'signals-2',
                                        text: 'I found this Confluence page that talks about how to offboard users from CI/CD',
                                    },
                                    {
                                        id: 'signals-3',
                                        text: 'I found a Slack channel that receives webhook notifications about builds',
                                    },
                                ],
                            },
                            {
                                id: 'knowledge-db',
                                title: 'Knowledge Database Enriched',
                                subtitle: 'Storing knowledge with citations',
                                sources: [],
                                steps: [
                                    {
                                        id: 'knowledge-db-1',
                                        text: 'I stored the knowledge found with citations in the knowledge database',
                                    },
                                ],
                            },
                            {
                                id: 'insight',
                                title: 'Correlated Insight',
                                subtitle: 'Synthesizing patterns and relationships',
                                sources: ['Okta'],
                                steps: [
                                    {
                                        id: 'insight-1',
                                        text: 'I ran a query to understand how API tokens are being generated and found that there is a user agent (python) that\'s generating tokens as well as human users. I looked at other detections and determined I could verify the users were admins using Okta groups',
                                    },
                                ],
                            },
                            {
                                id: 'detection',
                                title: 'Detection Created',
                                subtitle: 'Detection ready for review',
                                sources: [],
                                steps: [
                                    {
                                        id: 'detection-1',
                                        text: 'I created a detection that takes into account the behavior and adheres to your detection standard. It\'s verified and tested and available for review here (https://detection.respondnt.io/review/abc123)',
                                    },
                                ],
                            },
                            {
                                id: 'deployment',
                                title: 'Detection Deployed',
                                subtitle: 'Detection activated in production',
                                sources: [],
                                steps: [
                                    {
                                        id: 'deployment-1',
                                        text: 'Detection deployed…',
                                    },
                                ],
                            },
                            {
                                id: 'attack-response',
                                title: 'Attack Detected and Playbook Activated',
                                subtitle: 'Automated response in action',
                                sources: [],
                                steps: [
                                    {
                                        id: 'attack-response-1',
                                        text: 'Attack detected and Playbook activated',
                                    },
                                ],
                            },
                        ]}
                        stepInterval={1500}
                        autoStart={false}
                        showHeader={true}
                        onComplete={handleComplete}
                    />
                </div>
            </div>
        </section>
    );
};

/**
 * Storyboard Panel Component
 * 
 * Creates a minimal storyboard panel aligned with the design system
 * Available as an alternative style option
 */
// eslint-disable-next-line no-unused-vars
const StoryboardPanel = ({ number, content }) => {
    return (
        <div className="relative bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200 h-full flex flex-col">
            {/* Panel number badge */}
            <div className="absolute -top-3 -left-3 w-10 h-10 bg-accent-primary text-white rounded-full flex items-center justify-center font-bold text-sm font-mono shadow-sm z-10">
                {number}
            </div>
            {/* Panel content */}
            <p className="text-body md:text-body-lg font-sans text-gray-900 dark:text-gray-100 leading-relaxed pt-2 flex-1">
                {content}
            </p>
        </div>
    );
};

/**
 * Timeline Step Component
 * 
 * Creates a vertical timeline step with connecting line
 * Available as an alternative style option
 */
// eslint-disable-next-line no-unused-vars
const TimelineStep = ({ number, content, isLast = false }) => {
    return (
        <div className="relative flex gap-6 pb-8">
            {/* Timeline line and dot */}
            <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-accent-primary text-white rounded-full flex items-center justify-center font-bold text-sm font-mono shadow-sm z-10 flex-shrink-0">
                    {number}
                </div>
                {!isLast && (
                    <div className="w-0.5 bg-gray-300 dark:bg-gray-700 flex-1 mt-4 min-h-[60px]"></div>
                )}
            </div>
            {/* Content card */}
            <div className="flex-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200">
                <p className="text-body md:text-body-lg font-sans text-gray-900 dark:text-gray-100 leading-relaxed">
                    {content}
                </p>
            </div>
        </div>
    );
};

/**
 * Journey Phase Component
 * 
 * Groups steps into logical phases with visual separation
 * Available as an alternative style option
 */
const JourneyPhase = ({ phase, title, description, steps }) => {
    return (
        <div className="mb-16 last:mb-0">
            {/* Phase header */}
            <div className="mb-8 text-center">
                <div className="inline-flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-accent-primary/10 dark:bg-accent-primary/20 rounded-full flex items-center justify-center">
                        <span className="text-accent-primary font-bold text-lg font-mono">{phase}</span>
                    </div>
                    <h3 className="text-h3 md:text-h2 font-display font-bold text-gray-900 dark:text-white">
                        {title}
                    </h3>
                </div>
                {description && (
                    <p className="text-body-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                        {description}
                    </p>
                )}
            </div>
            {/* Steps grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {steps.map((step, index) => (
                    <div
                        key={index}
                        className="relative bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200"
                    >
                        <div className="absolute -top-3 -left-3 w-8 h-8 bg-accent-primary text-white rounded-full flex items-center justify-center font-bold text-xs font-mono shadow-sm z-10">
                            {index + 1}
                        </div>
                        <p className="text-body font-sans text-gray-900 dark:text-gray-100 leading-relaxed pt-1">
                            {step}
                        </p>
                    </div>
                ))}
            </div>
        </div>
    );
};

/**
 * Animated Journey Phases Component
 * 
 * Cycles through phases with fade animations and repeats
 * Available as an alternative style option
 */
// eslint-disable-next-line no-unused-vars
const AnimatedJourneyPhases = ({ phases, phaseDuration = 5000, transitionDuration = 500 }) => {
    const [currentPhaseIndex, setCurrentPhaseIndex] = React.useState(0);
    const [isVisible, setIsVisible] = React.useState(true);

    React.useEffect(() => {
        const cyclePhases = () => {
            // Fade out current phase
            setIsVisible(false);

            // After fade out completes, switch to next phase and fade in
            setTimeout(() => {
                setCurrentPhaseIndex((prevIndex) => {
                    const nextIndex = (prevIndex + 1) % phases.length;
                    return nextIndex;
                });
                // Fade in new phase
                setIsVisible(true);
            }, transitionDuration);
        };

        // Start the cycle after the initial phase has been displayed
        const interval = setInterval(cyclePhases, phaseDuration + transitionDuration);

        return () => clearInterval(interval);
    }, [phases.length, phaseDuration, transitionDuration]);

    const currentPhase = phases[currentPhaseIndex];

    return (
        <div className="relative min-h-[400px]">
            <div
                className={`transition-opacity ${isVisible ? 'opacity-100' : 'opacity-0'
                    }`}
                style={{
                    transitionDuration: `${transitionDuration}ms`,
                    transitionTimingFunction: 'ease-in-out'
                }}
            >
                <JourneyPhase
                    phase={currentPhase.phase}
                    title={currentPhase.title}
                    description={currentPhase.description}
                    steps={currentPhase.steps}
                />
            </div>
        </div>
    );
};

/**
 * Interactive Stepper Component
 * 
 * Shows one step at a time with navigation
 * Available as an alternative style option
 */
// eslint-disable-next-line no-unused-vars
const InteractiveStepper = ({ steps, currentStep, onStepChange }) => {
    const [activeStep, setActiveStep] = React.useState(currentStep || 0);

    const handleNext = () => {
        if (activeStep < steps.length - 1) {
            setActiveStep(activeStep + 1);
            onStepChange?.(activeStep + 1);
        }
    };

    const handlePrev = () => {
        if (activeStep > 0) {
            setActiveStep(activeStep - 1);
            onStepChange?.(activeStep - 1);
        }
    };

    return (
        <div className="max-w-4xl mx-auto">
            {/* Progress indicator */}
            <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                    <span className="text-body-sm text-gray-600 dark:text-gray-400 font-mono">
                        Step {activeStep + 1} of {steps.length}
                    </span>
                    <div className="flex gap-2">
                        <button
                            onClick={handlePrev}
                            disabled={activeStep === 0}
                            className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg font-medium transition-all duration-200 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            Previous
                        </button>
                        <button
                            onClick={handleNext}
                            disabled={activeStep === steps.length - 1}
                            className="px-4 py-2 bg-accent-primary text-white rounded-lg font-medium transition-all duration-200 hover:shadow-md hover:shadow-accent-primary/30 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            Next
                        </button>
                    </div>
                </div>
                {/* Progress bar */}
                <div className="w-full h-2 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
                    <div
                        className="h-full bg-accent-primary transition-all duration-300"
                        style={{ width: `${((activeStep + 1) / steps.length) * 100}%` }}
                    ></div>
                </div>
            </div>

            {/* Current step card */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-8 shadow-md">
                <div className="flex items-start gap-6 mb-6">
                    <div className="w-16 h-16 bg-accent-primary text-white rounded-full flex items-center justify-center font-bold text-lg font-mono shadow-sm flex-shrink-0">
                        {activeStep + 1}
                    </div>
                    <div className="flex-1">
                        <p className="text-body-lg md:text-h4 font-sans text-gray-900 dark:text-gray-100 leading-relaxed">
                            {steps[activeStep]}
                        </p>
                    </div>
                </div>
            </div>

            {/* Step indicators */}
            <div className="flex justify-center gap-2 mt-8">
                {steps.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => {
                            setActiveStep(index);
                            onStepChange?.(index);
                        }}
                        className={`w-2 h-2 rounded-full transition-all duration-200 ${index === activeStep
                            ? 'bg-accent-primary w-8'
                            : 'bg-gray-300 dark:bg-gray-700 hover:bg-gray-400 dark:hover:bg-gray-600'
                            }`}
                        aria-label={`Go to step ${index + 1}`}
                    />
                ))}
            </div>
        </div>
    );
};

/**
 * Story Section Component
 * 
 * Use this component to add new parts to your scrolling story.
 * 
 * Props:
 * - title: (string) Section heading
 * - content: (string) Main paragraph text
 * - alignment: 'center' | 'left' | 'right' (default: 'center')
 * - variant: 'default' | 'muted' | 'accent' (default: 'default')
 * - showDivider: (boolean) Show divider line above section (default: false)
 * - children: (ReactNode) Custom content (overrides content prop)
 * 
 * Examples:
 * 
 * Simple section:
 * <StorySection title="Part 1" content="Your story text here" showDivider={true} />
 * 
 * Left-aligned with muted text:
 * <StorySection title="Part 2" content="Story continues..." alignment="left" variant="muted" showDivider={true} />
 * 
 * Custom content:
 * <StorySection title="Part 3" showDivider={true}>
 *   <div>Your custom HTML/JSX here</div>
 * </StorySection>
 */
const StorySection = ({
    title,
    content,
    alignment = 'center',
    variant = 'default',
    showDivider = false,
    children
}) => {
    const alignmentClasses = {
        center: 'text-center',
        left: 'text-left',
        right: 'text-right'
    };

    const variantClasses = {
        default: 'text-gray-900 dark:text-white',
        muted: 'text-gray-700 dark:text-gray-300',
        accent: 'text-accent-primary'
    };

    return (
        <section className={`py-16 lg:py-24 ${alignmentClasses[alignment]}`}>
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                {showDivider && (
                    <div className="h-px bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-700 to-transparent mb-16 lg:mb-24 max-w-2xl mx-auto"></div>
                )}
                {title && (
                    <h2 className="text-h2 md:text-h1 font-display font-bold mb-6 text-gray-900 dark:text-white">
                        {title}
                    </h2>
                )}
                {content && (
                    <p className={`text-body-lg md:text-h3 font-sans leading-relaxed max-w-4xl ${alignment === 'center' ? 'mx-auto' :
                        alignment === 'right' ? 'ml-auto' :
                            'mr-auto'
                        } ${variantClasses[variant]}`}>
                        {content}
                    </p>
                )}
                {children}
            </div>
        </section>
    );
};

/**
 * Story Section Columns Component
 * 
 * Displays story content in columns, each representing a different user role.
 * 
 * Props:
 * - title: (string) Section heading
 * - cisoContent: (string) Content from CISO perspective
 * - detectionLeadContent: (string) Content from Detection Lead perspective
 * - detectionEngineerContent: (string) Content from Detection Engineer perspective
 * - showDivider: (boolean) Show divider line above section (default: false)
 */
const StorySectionColumns = ({
    title,
    cisoContent,
    detectionLeadContent,
    detectionEngineerContent,
    showDivider = false
}) => {
    // Calculate number of columns based on available content
    const columnCount = [cisoContent, detectionLeadContent, detectionEngineerContent].filter(Boolean).length;
    const gridColsClass = columnCount === 3 ? 'md:grid-cols-3' : columnCount === 2 ? 'md:grid-cols-2' : 'md:grid-cols-1';

    return (
        <section className="py-16 lg:py-24">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                {showDivider && (
                    <div className="h-px bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-700 to-transparent mb-16 lg:mb-24 max-w-2xl mx-auto"></div>
                )}
                {title && (
                    <h2 className="text-h2 md:text-h1 font-display font-bold mb-12 text-center text-gray-900 dark:text-white">
                        {title}
                    </h2>
                )}
                <div className={`grid grid-cols-1 gap-6 lg:gap-8 ${gridColsClass}`}>
                    {/* CISO Column */}
                    {cisoContent && <div className="space-y-4">
                        <div className="mb-4">
                            <h3 className="text-h4 md:text-h3 font-display font-bold text-gray-900 dark:text-white mb-2">
                                CISO
                            </h3>
                            <div className="w-12 h-1 bg-accent-primary rounded-full"></div>
                        </div>
                        <p className="text-body-lg md:text-h4 font-sans leading-relaxed text-gray-700 dark:text-gray-300">
                            {cisoContent}
                        </p>
                    </div>}

                    {/* Detection Lead Column */}
                    {detectionLeadContent && <div className="space-y-4">
                        <div className="mb-4">
                            <h3 className="text-h4 md:text-h3 font-display font-bold text-gray-900 dark:text-white mb-2">
                                Detection Lead
                            </h3>
                            <div className="w-12 h-1 bg-accent-primary rounded-full"></div>
                        </div>
                        <p className="text-body-lg md:text-h4 font-sans leading-relaxed text-gray-700 dark:text-gray-300">
                            {detectionLeadContent}
                        </p>
                    </div>}

                    {/* Detection Engineer Column */}
                    <div className="space-y-4">
                        <div className="mb-4">
                            <h3 className="text-h4 md:text-h3 font-display font-bold text-gray-900 dark:text-white mb-2">
                                Detection Engineer
                            </h3>
                            <div className="w-12 h-1 bg-accent-primary rounded-full"></div>
                        </div>
                        <p className="text-body-lg md:text-h4 font-sans leading-relaxed text-gray-700 dark:text-gray-300">
                            {detectionEngineerContent}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
};

function App() {
    const vantaRef = useRef(null);
    const vantaEffect = useRef(null);

    useEffect(() => {
        if (!vantaEffect.current && window.THREE && vantaRef.current) {
            vantaEffect.current = NET({
                el: vantaRef.current,
                mouseControls: false,
                touchControls: false,
                gyroControls: false,
                minHeight: 200.00,
                minWidth: 200.00,
                backgroundColor: 0xfafafa, // Light gray background (gray-50)
                color: 0x00d4ff, // accent-primary cyan
                color2: 0x7c3aed, // accent-secondary purple (optional)
                size: 1.00,
                spacing: 20.00,
                showLines: true
            });
        }

        return () => {
            if (vantaEffect.current) {
                vantaEffect.current.destroy();
                vantaEffect.current = null;
            }
        };
    }, []);

    return (
        <div className="min-h-screen bg-white dark:bg-gray-950">
            {/* Floating CTA Link - Top Right */}
            <a
                href="https://analyze.respondnt.io/"
                target="_blank"
                rel="noopener noreferrer"
                className="fixed top-4 right-4 md:top-6 md:right-6 z-50 px-4 py-2.5 md:px-6 md:py-3 bg-accent-primary text-white rounded-lg font-medium text-body-xs md:text-body-sm transition-all duration-200 hover:shadow-md hover:shadow-accent-primary/30 hover:scale-105 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary/60 focus-visible:ring-offset-2 shadow-sm"
            >
                Preview MITRE Tranposition
            </a>

            {/* Hero Section with Vanta Background */}
            <section className="min-h-screen flex items-center justify-center relative overflow-hidden">
                {/* Vanta Background - Futuristic minimal halo effect - only in hero section */}
                <div
                    ref={vantaRef}
                    className="absolute inset-0 z-0 pointer-events-none"
                />

                {/* Content */}
                <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center relative z-10">
                    <h1 className="text-display-lg md:text-display-xl lg:text-display-2xl font-display font-bold text-gray-900 dark:text-white leading-tight mb-6 flex items-center justify-center gap-4 md:gap-6 mx-auto w-fit -translate-x-3">
                        <img
                            src="/logo.png"
                            alt="Respondnt Logo"
                            className="h-12 md:h-16 lg:h-20 w-auto"
                            style={{ display: 'block' }}
                        />
                        <span>Respondnt</span>
                    </h1>
                    <p className="text-h2 md:text-h1 font-sans font-medium text-gray-700 dark:text-gray-300 leading-relaxed max-w-4xl mx-auto">
                        Keeps your organization secure by continuously assessing and improving your threat coverage, controls and detections
                    </p>
                </div>
            </section>

            {/* The Story */}
            <StorySection
                title="Who is Respondnt for?"
                showDivider={true}
            >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mt-12">
                    {/* First Pillar - Existing Content */}
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200 h-full flex flex-col">
                        <div className="mb-4 flex items-center justify-center w-14 h-14 bg-accent-primary/10 dark:bg-accent-primary/20 rounded-lg mx-auto">
                            <svg className="w-7 h-7 text-accent-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                            </svg>
                        </div>
                        <p className="text-body font-sans leading-relaxed text-gray-700 dark:text-gray-300 flex-1 text-center">
                            You are currently investing in or thinking about starting to build a custom detection program for SaaS, Cloud and Identity but are struggling with the high cost and complexity of building and maintaining it
                        </p>
                    </div>

                    {/* Second Pillar - New Area/Technology */}
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200 h-full flex flex-col">
                        <div className="mb-4 flex items-center justify-center w-14 h-14 bg-accent-primary/10 dark:bg-accent-primary/20 rounded-lg mx-auto">
                            <svg className="w-7 h-7 text-accent-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                            </svg>
                        </div>
                        <p className="text-body font-sans leading-relaxed text-gray-700 dark:text-gray-300 flex-1 text-center">
                            You are looking to modernize your detection program by shifting to detections as code, continuous gap analysis and threat coverage, and looking for a tool with native support
                        </p>
                    </div>

                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200 h-full flex flex-col">
                        <div className="mb-4 flex items-center justify-center w-14 h-14 bg-accent-primary/10 dark:bg-accent-primary/20 rounded-lg mx-auto">
                            <svg className="w-7 h-7 text-accent-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                        <p className="text-body font-sans leading-relaxed text-gray-700 dark:text-gray-300 flex-1 text-center">
                            You are looking for solutions that integrate with your existing detection tools and systems
                        </p>
                    </div>
                </div>
            </StorySection>

            {/* Platform Features Section */}
            <section className="py-16 lg:py-24 bg-gray-50 dark:bg-gray-900">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="mb-12 text-center">
                        <h2 className="text-h2 md:text-h1 font-display font-bold text-gray-900 dark:text-white mb-4">
                            The Respondnt Platform
                        </h2>
                        <p className="text-body-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                            Modernize your detection program with comprehensive threat coverage and seamless integration
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
                        {/* Column 1: Detections as Code and Gap Analysis */}
                        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200 h-full flex flex-col">
                            <div className="mb-4 flex items-center justify-center w-14 h-14 bg-accent-primary/10 dark:bg-accent-primary/20 rounded-lg mx-auto">
                                <svg className="w-7 h-7 text-accent-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                                </svg>
                            </div>
                            <h3 className="text-h4 font-display font-bold text-gray-900 dark:text-white mb-3 text-center">
                                Detections as Code and Gap Analysis
                            </h3>
                            <p className="text-body font-sans leading-relaxed text-gray-700 dark:text-gray-300 flex-1 text-center">
                                Shift to detections as code with continuous gap analysis. Automatically identify coverage gaps in your detection program and maintain your detections as version-controlled code for better collaboration and deployment.
                            </p>
                        </div>

                        {/* Column 2: Continuous Threat Modelling and Detection Opportunity Analysis */}
                        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200 h-full flex flex-col">
                            <div className="mb-4 flex items-center justify-center w-14 h-14 bg-accent-primary/10 dark:bg-accent-primary/20 rounded-lg mx-auto">
                                <svg className="w-7 h-7 text-accent-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                            </div>
                            <h3 className="text-h4 font-display font-bold text-gray-900 dark:text-white mb-3 text-center">
                                Continuous Threat Modelling and Detection Opportunity Analysis
                            </h3>
                            <p className="text-body font-sans leading-relaxed text-gray-700 dark:text-gray-300 flex-1 text-center">
                                Continuously model threats and analyze detection opportunities. Our platform automatically identifies new attack surfaces, updates threat models, and highlights opportunities to improve your detection coverage.
                            </p>
                        </div>

                        {/* Column 3: Deploy Coverage by Integrating */}
                        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200 h-full flex flex-col">
                            <div className="mb-4 flex items-center justify-center w-14 h-14 bg-accent-primary/10 dark:bg-accent-primary/20 rounded-lg mx-auto">
                                <svg className="w-7 h-7 text-accent-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <h3 className="text-h4 font-display font-bold text-gray-900 dark:text-white mb-3 text-center">
                                Deploy Coverage by Integrating
                            </h3>
                            <p className="text-body font-sans leading-relaxed text-gray-700 dark:text-gray-300 flex-1 text-center">
                                Seamlessly integrate into your existing environment. Deploy detections directly to Splunk, Panther, Google SecOps. Output threat models, detections, and research to your existing tools and systems without disrupting your workflow.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* How Respondnt Works Section */}
            <section className="py-16 lg:py-24 bg-white dark:bg-gray-950">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="mb-12 text-center">
                        <h2 className="text-h2 md:text-h1 font-display font-bold text-gray-900 dark:text-white mb-4">
                            How does Respondnt do this for Cloud, SaaS and Identity?
                        </h2>
                        <p className="text-body-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto mb-8">
                            Our agentic workflow operates continuously or on-demand, ensuring your security posture evolves with your organization. From initial onboarding to continuous coverage, Respondnt's intelligent agents work together to assess and improve your threat coverage across all your applications. The platform adapts to your environment by incorporating context and input from your existing tools and systems.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-8">
                        {/* SaaS and Identity Card */}
                        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200 h-full flex flex-col">
                            <div className="mb-4 flex items-center justify-center w-14 h-14 bg-accent-primary/10 dark:bg-accent-primary/20 rounded-lg">
                                <svg className="w-7 h-7 text-accent-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <h3 className="text-h4 font-display font-bold text-gray-900 dark:text-white mb-3">
                                SaaS and Identity
                            </h3>
                            <p className="text-body font-sans leading-relaxed text-gray-700 dark:text-gray-300 flex-1">
                                Our agentic workflow understands and interprets the core functionality in an application, the potential attack paths, and telemetry available to uncover blind spots and detection opportunities
                            </p>
                        </div>

                        {/* Cloud Card */}
                        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200 h-full flex flex-col">
                            <div className="mb-4 flex items-center justify-center w-14 h-14 bg-accent-primary/10 dark:bg-accent-primary/20 rounded-lg">
                                <svg className="w-7 h-7 text-accent-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                                </svg>
                            </div>
                            <h3 className="text-h4 font-display font-bold text-gray-900 dark:text-white mb-3">
                                Cloud
                            </h3>
                            <p className="text-body font-sans leading-relaxed text-gray-700 dark:text-gray-300 flex-1">
                                Our agentic workflow uses Terraform and code to understand your 1st party applications at an infrastructure level and application level to generate attack paths, uncover blind spots, and detection opportunities
                            </p>
                        </div>
                    </div>

                    {/* What do you get in the output */}
                    <div className="mt-16 lg:mt-24">
                        <div className="mb-12 text-center">
                            <h3 className="text-h2 md:text-h1 font-display font-bold text-gray-900 dark:text-white mb-4">
                                What Respondnt delivers
                            </h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                            {/* Item 1: MITRE Attack Mapping */}
                            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200 h-full flex flex-col">
                                <div className="mb-4 flex items-center justify-center w-12 h-12 bg-accent-primary/10 dark:bg-accent-primary/20 rounded-lg">
                                    <svg className="w-6 h-6 text-accent-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                                    </svg>
                                </div>
                                <p className="text-body font-sans leading-relaxed text-gray-700 dark:text-gray-300 flex-1">
                                    An ongoing transposition of all your applications and detections onto MITRE ATT&CK
                                </p>
                            </div>

                            {/* Item 2: Threat Model as Code */}
                            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200 h-full flex flex-col">
                                <div className="mb-4 flex items-center justify-center w-12 h-12 bg-accent-primary/10 dark:bg-accent-primary/20 rounded-lg">
                                    <svg className="w-6 h-6 text-accent-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                                    </svg>
                                </div>
                                <p className="text-body font-sans leading-relaxed text-gray-700 dark:text-gray-300 flex-1">
                                    A continuous, up-to-date threat model as code or on the platform per application
                                </p>
                            </div>

                            {/* Item 3: Tested and Validated Detections with Deployments */}
                            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200 h-full flex flex-col">
                                <div className="mb-4 flex items-center justify-center w-12 h-12 bg-accent-primary/10 dark:bg-accent-primary/20 rounded-lg">
                                    <svg className="w-6 h-6 text-accent-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <p className="text-body font-sans leading-relaxed text-gray-700 dark:text-gray-300 flex-1">
                                    Tested and validated detections as code presented as opportunities to your team, with single-click deployments to your environment
                                </p>
                            </div>

                            {/* Item 4: Telemetry Insights */}
                            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200 h-full flex flex-col">
                                <div className="mb-4 flex items-center justify-center w-12 h-12 bg-accent-primary/10 dark:bg-accent-primary/20 rounded-lg">
                                    <svg className="w-6 h-6 text-accent-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                </div>
                                <p className="text-body font-sans leading-relaxed text-gray-700 dark:text-gray-300 flex-1">
                                    Telemetry insights that help you guide what you need to ingest, what you don't need to ingest, and data engineering insights you can use to improve your detection program
                                </p>
                            </div>

                            {/* Item 5: Investigative Assistant */}
                            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200 h-full flex flex-col">
                                <div className="mb-4 flex items-center justify-center w-12 h-12 bg-accent-primary/10 dark:bg-accent-primary/20 rounded-lg">
                                    <svg className="w-6 h-6 text-accent-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                                    </svg>
                                </div>
                                <p className="text-body font-sans leading-relaxed text-gray-700 dark:text-gray-300 flex-1">
                                    An investigative assistant with access to the same toolsets we use to perform this analysis to assist with ad hoc processes
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Contact Form Section */}
            <ContactFormSection />
        </div>
    );
}


/**
 * Contact Form Section Component
 * 
 * Form that submits to Google Sheets via Google Apps Script
 */
const ContactFormSection = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        company: '',
        role: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
    const [errorMessage, setErrorMessage] = useState('');

    // Replace with your Google Apps Script Web App URL
    const GOOGLE_SCRIPT_URL = "https://script.google.com/macros/s/AKfycbwZ0nhPXFBsLKJC1L3Rqh4KRl2x1HaP-IOlVp6eV6Rztu298f5Pb21fHnazge5xI-vh/exec";


    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus(null);
        setErrorMessage('');

        try {
            const formDataToSend = new URLSearchParams();
            Object.keys(formData).forEach(key => {
                formDataToSend.append(key, formData[key]);
            });

            const response = await fetch(GOOGLE_SCRIPT_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: formDataToSend
            });

            if (response.ok) {
                const result = await response.json();
                if (result.success) {
                    setSubmitStatus('success');
                    setFormData({
                        name: '',
                        email: '',
                        company: '',
                        role: '',
                        message: ''
                    });

                    // Reset success message after 5 seconds
                    setTimeout(() => {
                        setSubmitStatus(null);
                    }, 5000);
                } else {
                    throw new Error(result.message || 'Submission failed');
                }
            } else {
                throw new Error('Server error');
            }
        } catch (error) {
            setSubmitStatus('error');
            setErrorMessage(error.message || 'There was an error submitting your form. Please try again.');
            console.error('Form submission error:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section className="py-16 lg:py-24 bg-gray-50 dark:bg-gray-950">
            <div className="max-w-4xl mx-auto px-6 lg:px-8">
                <div className="mb-12 text-center">
                    <h2 className="text-h2 md:text-h1 font-display font-bold text-gray-900 dark:text-white mb-4">
                        Get in Touch
                    </h2>
                    <p className="text-body-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                        Interested in learning more? Get in touch with us below
                    </p>
                </div>

                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm overflow-hidden">
                    <form onSubmit={handleSubmit} className="p-6 lg:p-8 space-y-6">
                        {/* Name Field */}
                        <div className="space-y-2">
                            <label htmlFor="name" className="block text-body-sm font-medium text-gray-700 dark:text-gray-300">
                                Name <span className="text-accent-error">*</span>
                            </label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                required
                                value={formData.name}
                                onChange={handleChange}
                                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-body bg-white dark:bg-gray-900 text-gray-900 dark:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-accent-primary/20 focus:border-accent-primary"
                                placeholder="Your full name"
                            />
                        </div>

                        {/* Email Field */}
                        <div className="space-y-2">
                            <label htmlFor="email" className="block text-body-sm font-medium text-gray-700 dark:text-gray-300">
                                Email <span className="text-accent-error">*</span>
                            </label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                required
                                value={formData.email}
                                onChange={handleChange}
                                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-body bg-white dark:bg-gray-900 text-gray-900 dark:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-accent-primary/20 focus:border-accent-primary"
                                placeholder="your.email@example.com"
                            />
                        </div>

                        {/* Company Field */}
                        <div className="space-y-2">
                            <label htmlFor="company" className="block text-body-sm font-medium text-gray-700 dark:text-gray-300">
                                Company
                            </label>
                            <input
                                type="text"
                                id="company"
                                name="company"
                                value={formData.company}
                                onChange={handleChange}
                                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-body bg-white dark:bg-gray-900 text-gray-900 dark:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-accent-primary/20 focus:border-accent-primary"
                                placeholder="Your company name"
                            />
                        </div>

                        {/* Role Field */}
                        <div className="space-y-2">
                            <label htmlFor="role" className="block text-body-sm font-medium text-gray-700 dark:text-gray-300">
                                Role
                            </label>
                            <input
                                type="text"
                                id="role"
                                name="role"
                                value={formData.role}
                                onChange={handleChange}
                                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-body bg-white dark:bg-gray-900 text-gray-900 dark:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-accent-primary/20 focus:border-accent-primary"
                                placeholder="Your job title"
                            />
                        </div>

                        {/* Message Field */}
                        <div className="space-y-2">
                            <label htmlFor="message" className="block text-body-sm font-medium text-gray-700 dark:text-gray-300">
                                Message
                            </label>
                            <textarea
                                id="message"
                                name="message"
                                rows={5}
                                value={formData.message}
                                onChange={handleChange}
                                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-body bg-white dark:bg-gray-900 text-gray-900 dark:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-accent-primary/20 focus:border-accent-primary resize-none"
                                placeholder="Tell us about your security challenges..."
                            />
                        </div>

                        {/* Submit Button */}
                        <div className="pt-4">
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full px-6 py-3 bg-accent-primary text-white rounded-lg font-medium transition-all duration-200 hover:shadow-md hover:shadow-accent-primary/30 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                {isSubmitting ? 'Submitting...' : 'Submit'}
                            </button>
                        </div>

                        {/* Status Messages */}
                        {submitStatus === 'success' && (
                            <div className="p-4 bg-accent-success/10 border border-accent-success/20 rounded-lg">
                                <p className="text-body-sm text-accent-success">
                                    Thank you for your interest! We'll be in touch soon.
                                </p>
                            </div>
                        )}

                        {submitStatus === 'error' && (
                            <div className="p-4 bg-accent-error/10 border border-accent-error/20 rounded-lg">
                                <p className="text-body-sm text-accent-error">
                                    {errorMessage || 'There was an error submitting your form. Please try again.'}
                                </p>
                            </div>
                        )}
                    </form>
                </div>
            </div>
        </section>
    );
};

export default App;
